import { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CustomFormV3 } from '../../../shared/CustomFormV3/CustomForm';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import { useInjuryEndpoints } from '../../../hooks/useInjuryEndpoints';
import { IInjury, ICreateInjuryDto, IUpdateInjuryDto } from '../../../models/injury';
import {
  INJURY_BODY_ZONE,
  INJURY_ONSET,
  INJURY_SEVERITY,
  INJURY_CONTEXT,
  INJURY_MECHANISM,
  INJURY_TYPE_EXTENDED,
} from '../../../enums/injuries';
import { LATERALITY, INJURY_TYPE } from '../../../enums/testing';
import { INJURY_FORM_LAYOUT } from './InjuryRegistrationForm.constants';
import styles from './InjuryRegistrationForm.module.scss';

/** Dati di prefill provenienti dalla body map interattiva (fastidio → apertura infortunio). */
export interface PrefillInjuryData {
  bodyZone?: string;
  injuryDate?: string;
  description?: string;
  sourceBodyMapComplaintId?: string;
}

/** Valori tipizzati del form — sostituisce il precedente `useForm<any>`. */
export interface IInjuryFormValues {
  bodyZone: INJURY_BODY_ZONE | null;
  laterality: LATERALITY | null;
  injuryDate: Date | null;
  expectedReturnDate: Date | null;
  severityEstimated: INJURY_SEVERITY | null;
  injuryType: INJURY_TYPE_EXTENDED | null;
  onset: INJURY_ONSET | null;
  context: INJURY_CONTEXT | null;
  mechanism: INJURY_MECHANISM | null;
  description: string;
  diagnosis: string;
  treatingPhysio: string;
  createRtpProtocol: boolean;
  rtpInjuryType: INJURY_TYPE | null;
}

interface InjuryRegistrationFormProps {
  athleteId: string;
  teamId: string;
  editingInjury?: IInjury;
  prefillData?: PrefillInjuryData | null;
  onSuccess?: (injury: IInjury) => void;
  onCancel?: () => void;
  submitLabel?: string;
  /** ID HTML del form — usato dal modal per collegare il pulsante Submit esterno nel Dialog.Footer */
  formId?: string;
  /** Callback invocata ogni volta che isSubmitting cambia — usata dal modal per disabilitare il pulsante esterno */
  onSubmittingChange?: (isSubmitting: boolean) => void;
  /** Se true non renderizza i pulsanti inline (li gestisce il contenitore esterno via Dialog.Footer) */
  hideButtons?: boolean;
}

export const InjuryRegistrationForm: FC<InjuryRegistrationFormProps> = ({
  athleteId,
  teamId,
  editingInjury,
  prefillData,
  onSuccess,
  onCancel,
  submitLabel = 'Registra infortunio',
  formId,
  onSubmittingChange,
  hideButtons = false,
}) => {
  const injuryEndpoints = useInjuryEndpoints();
  const queryClient = useQueryClient();

  const BODY_MAP_TO_INJURY_ZONE: Record<string, INJURY_BODY_ZONE> = {
    head_neck: INJURY_BODY_ZONE.TESTA,
    shoulder_dx: INJURY_BODY_ZONE.SPALLA_DX,
    shoulder_sx: INJURY_BODY_ZONE.SPALLA_SX,
    chest_ribs: INJURY_BODY_ZONE.TORACE,
    elbow_dx: INJURY_BODY_ZONE.GOMITO_DX,
    elbow_sx: INJURY_BODY_ZONE.GOMITO_SX,
    wrist_hand_dx: INJURY_BODY_ZONE.POLSO_DX,
    wrist_hand_sx: INJURY_BODY_ZONE.POLSO_SX,
    upper_back: INJURY_BODY_ZONE.SCHIENA_ALTA,
    lumbar: INJURY_BODY_ZONE.SCHIENA_BASSA,
    hip_groin_dx: INJURY_BODY_ZONE.ANCA_DX,
    hip_groin_sx: INJURY_BODY_ZONE.ANCA_SX,
    quadricep_dx: INJURY_BODY_ZONE.COSCIA_DX,
    quadricep_sx: INJURY_BODY_ZONE.COSCIA_SX,
    hamstring_dx: INJURY_BODY_ZONE.HAMSTRING_DX,
    hamstring_sx: INJURY_BODY_ZONE.HAMSTRING_SX,
    knee_dx: INJURY_BODY_ZONE.GINOCCHIO_DX,
    knee_sx: INJURY_BODY_ZONE.GINOCCHIO_SX,
    calf_dx: INJURY_BODY_ZONE.POLPACCIO_DX,
    calf_sx: INJURY_BODY_ZONE.POLPACCIO_SX,
    ankle_foot_dx: INJURY_BODY_ZONE.CAVIGLIA_DX,
    ankle_foot_sx: INJURY_BODY_ZONE.CAVIGLIA_SX,
  };

  const buildPrefillBodyZone = (zone?: string): INJURY_BODY_ZONE | null => {
    if (!zone) return null;
    return BODY_MAP_TO_INJURY_ZONE[zone] ?? (Object.values(INJURY_BODY_ZONE).includes(zone as INJURY_BODY_ZONE) ? zone as INJURY_BODY_ZONE : null);
  };

  const methods = useForm<IInjuryFormValues>({
    defaultValues: editingInjury
      ? {
          bodyZone: editingInjury.bodyZone ?? null,
          laterality: editingInjury.laterality ?? editingInjury.side ?? null,
          injuryDate: editingInjury.injuryDate ? dayjs(editingInjury.injuryDate).toDate() : null,
          expectedReturnDate: editingInjury.expectedReturnDate ? dayjs(editingInjury.expectedReturnDate).toDate() : null,
          severityEstimated: editingInjury.severityEstimated ?? editingInjury.severity ?? null,
          injuryType: editingInjury.injuryType ?? null,
          onset: editingInjury.onset ?? null,
          context: editingInjury.context ?? null,
          mechanism: editingInjury.mechanism ?? null,
          description: editingInjury.description ?? '',
          diagnosis: editingInjury.diagnosis ?? '',
          treatingPhysio: editingInjury.treatingPhysio ?? '',
          createRtpProtocol: false,
          rtpInjuryType: null,
        }
      : prefillData
      ? {
          bodyZone: buildPrefillBodyZone(prefillData.bodyZone),
          laterality: null,
          injuryDate: prefillData.injuryDate ? dayjs(prefillData.injuryDate).toDate() : new Date(),
          expectedReturnDate: null,
          severityEstimated: null,
          injuryType: null,
          onset: null,
          context: null,
          mechanism: null,
          description: prefillData.description ?? '',
          diagnosis: '',
          treatingPhysio: '',
          createRtpProtocol: false,
          rtpInjuryType: null,
        }
      : {
          bodyZone: null,
          laterality: null,
          injuryDate: new Date(),
          expectedReturnDate: null,
          severityEstimated: null,
          injuryType: null,
          onset: null,
          context: null,
          mechanism: null,
          description: '',
          diagnosis: '',
          treatingPhysio: '',
          createRtpProtocol: false,
          rtpInjuryType: null,
        },
    // Validazione Yup: mostra errori inline sotto ai campi obbligatori invece
    // del toast generico "Compila i campi obbligatori" (ex-onSubmit manuale).
    resolver: yupResolver(
      yup.object({
        bodyZone: yup.string().nullable().required('La zona corporea è obbligatoria.'),
        injuryDate: yup.date().nullable().required('La data infortunio è obbligatoria.'),
        severityEstimated: yup.string().nullable().required('La gravità è obbligatoria.'),
        injuryType: yup.string().nullable().required('Il tipo di lesione è obbligatorio.'),
        onset: yup.string().nullable().required('L\'insorgenza è obbligatoria.'),
        // Il protocollo RTP (solo in creazione, non in modifica) richiede tipo
        // lesione RTP + lato — RtpProtocolDataDto.injurySide è required lato backend.
        rtpInjuryType: yup
          .string()
          .nullable()
          .test('rtp-injury-type-required', 'Indica il tipo di lesione per il protocollo RTP.', function (value) {
            const wantsRtp = !editingInjury && !!this.parent.createRtpProtocol;
            return !wantsRtp || !!value;
          }),
        laterality: yup
          .string()
          .nullable()
          .test('rtp-laterality-required', 'Indica il lato (Destra/Sinistra/Bilaterale) per il protocollo RTP.', function (value) {
            const wantsRtp = !editingInjury && !!this.parent.createRtpProtocol;
            return !wantsRtp || !!value;
          }),
      }),
    ) as any,
  });

  const { handleSubmit, formState: { isSubmitting } } = methods;

  // Notifica il contenitore esterno (es. Dialog.Footer) dei cambi di stato submitting
  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  const onSubmit = async (values: IInjuryFormValues) => {
    // Yup ha già garantito questi campi non-null: gli asserimenti servono solo a TS.
    const bodyZone = values.bodyZone!;
    const severity = values.severityEstimated!;
    const injuryType = values.injuryType!;
    const onset = values.onset!;

    // teamId è obbligatorio per la creazione (@IsMongoId sul DTO): senza, il backend
    // risponde 400 e l'utente vedrebbe solo "Errore durante il salvataggio".
    // Capita dal profilo atleta se l'atleta non è associato ad alcun team — vedi bug b3e591.
    if (!editingInjury && !teamId) {
      toast.error("Atleta non associato a nessun team: impossibile registrare l'infortunio.");
      return;
    }

    const wantsRtp = !editingInjury && !!values.createRtpProtocol;

    // Usare dayjs().format evita lo shift -1 giorno che toISOString() causa
    // su timezone UTC+ (es. Italy) quando il Date è mezzanotte locale.
    const toISO = (d: Date | null | undefined) =>
      d ? dayjs(d).format('YYYY-MM-DD') : undefined;

    try {
      let injury: IInjury;

      if (editingInjury) {
        const dto: IUpdateInjuryDto = {
          bodyZone,
          laterality: values.laterality ?? undefined,
          injuryDate: toISO(values.injuryDate),
          severityEstimated: severity,
          injuryType,
          onset,
          context: values.context ?? undefined,
          mechanism: values.mechanism ?? undefined,
          description: values.description || undefined,
          diagnosis: values.diagnosis || undefined,
          treatingPhysio: values.treatingPhysio || undefined,
          expectedReturnDate: toISO(values.expectedReturnDate),
        };
        const { data } = await injuryEndpoints.patchInjury(editingInjury._id, dto);
        injury = data;
        toast.success('Infortunio aggiornato');
      } else {
        const dto: ICreateInjuryDto = {
          athleteId,
          teamId,
          bodyZone,
          laterality: values.laterality ?? undefined,
          injuryDate: toISO(values.injuryDate)!,
          severityEstimated: severity,
          injuryType,
          onset,
          context: values.context ?? undefined,
          mechanism: values.mechanism ?? undefined,
          description: values.description || undefined,
          diagnosis: values.diagnosis || undefined,
          treatingPhysio: values.treatingPhysio || undefined,
          expectedReturnDate: toISO(values.expectedReturnDate),
          createRtpProtocol: !!values.createRtpProtocol,
          // RTP: il backend vuole l'oggetto NESTED `rtpProtocolData` (i campi flat
          // venivano strippati da whitelist:true → RTP mai creato). Vedi bug b3e591.
          ...(wantsRtp
            ? {
                rtpProtocolData: {
                  injuryType: values.rtpInjuryType!,
                  injurySide: values.laterality!,
                },
              }
            : {}),
          sourceBodyMapComplaintId: prefillData?.sourceBodyMapComplaintId || undefined,
        };
        const { data } = await injuryEndpoints.postInjury(dto);
        injury = data;
        toast.success('Infortunio registrato');
      }

      // Invalida le query correlate
      queryClient.invalidateQueries({ queryKey: ['athlete-injuries', athleteId] });
      queryClient.invalidateQueries({ queryKey: ['team-injuries', teamId] });
      queryClient.invalidateQueries({ queryKey: ['team-readiness', teamId] });

      onSuccess?.(injury);
    } catch (err: any) {
      // Mostra il messaggio reale del server (es. errori di validazione enum) invece
      // del generico, così un eventuale nuovo mismatch è diagnosticabile e non muto. (b3e591)
      const serverMsg = err?.response?.data?.message;
      toast.error(
        Array.isArray(serverMsg)
          ? serverMsg.join(' • ')
          : serverMsg || 'Errore durante il salvataggio',
      );
    }
  };

  return (
    <FormProvider {...methods}>
      {/* id collegato al pulsante Submit esterno nel Dialog.Footer tramite attributo form="..." */}
      <form id={formId} onSubmit={handleSubmit(onSubmit)}>
        <CustomFormV3 layout={INJURY_FORM_LAYOUT} />
        {/* Pulsanti inline — nascosti quando il modal gestisce il footer esternamente */}
        {!hideButtons && (
          <div className={styles.actions}>
            {onCancel && (
              <CustomButton color="outline" type="button" onClick={onCancel}>
                Annulla
              </CustomButton>
            )}
            <CustomButton color="secondary" type="submit" disabled={isSubmitting} isLoading={isSubmitting}>
              {submitLabel}
            </CustomButton>
          </div>
        )}
      </form>
    </FormProvider>
  );
};

export default InjuryRegistrationForm;
